import { Button, Form, InputNumber, Select } from 'antd';
import type { FormListFieldData } from 'antd/es/form';
import { DollarSign, Trash2 } from 'lucide-react';
import { ProductInfo } from 'src/types/common.types';
import { removeAccentCharacters } from 'src/utils/helpers';
import { formatNumberWithCommas } from './entry.utils';
import { ProductItem } from './entry.types';

export type ProductColumnsParams = {
  products: ProductInfo[] | undefined;
  formProducts: ProductItem[];
  onSearchProductBySku: (keyword: string, option: any) => boolean;
  onSearchProduct: (keyword: string, option: any) => boolean;
  onSelectProduct: (index: number, value: number, option: any) => void;
  remove: (index: number) => void;
};

export function getProductTableColumns({
  products,
  formProducts,
  onSearchProductBySku,
  onSearchProduct,
  onSelectProduct,
  remove,
}: ProductColumnsParams) {
  return (_fields: FormListFieldData[], _remove?: (index: number) => void) => [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 40,
      render: (_: number | undefined, _record: ProductItem, index: number) => <b className="mb-[40px]">{index + 1}</b>,
    },
    {
      title: '#',
      dataIndex: 'productId',
      key: 'productId',
      hidden: true,
      render: (_: number | undefined, _record: ProductItem, index: number) => (
        <>
          <Form.Item name={[index, 'productId']} noStyle hidden wrapperCol={{ span: 24 }}>
            <InputNumber />
          </Form.Item>
          <Form.Item name={[index, 'minPrice']} noStyle hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={[index, 'maxPrice']} noStyle hidden>
            <InputNumber />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Mã sản phẩm',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (_: string | undefined, _record: ProductItem, index: number) => (
        <Form.Item
          name={[index, 'code']}
          className="w-full"
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: 'Vui lòng nhập mã sản phẩm' }]}
          noStyle
        >
          <Select
            className="w-full"
            popupMatchSelectWidth={false}
            placeholder="SKU"
            options={products?.map((product: ProductInfo) => ({
              label: product.code,
              name: product.name,
              code: product.code,
              vat: product.vat,
              minPrice: product.minPrice,
              maxPrice: product.maxPrice,
              value: product.id,
              searchname: removeAccentCharacters(product.name).toLocaleLowerCase(),
            }))}
            showSearch
            filterOption={onSearchProductBySku}
            onSelect={(value, option) => onSelectProduct(index, value, option)}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'name',
      key: 'name',
      render: (_: string | undefined, _record: ProductItem, index: number) => (
        <Form.Item
          name={[index, 'name']}
          className=" w-full"
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
          noStyle
        >
          <Select
            placeholder="Tên sản phẩm"
            options={products?.map((product: ProductInfo) => ({
              label: product.name,
              name: product.name,
              code: product.code,
              vat: product.vat,
              minPrice: product.minPrice,
              maxPrice: product.maxPrice,
              value: product.id,
              searchname: removeAccentCharacters(product.name).toLocaleLowerCase(),
            }))}
            showSearch
            filterOption={onSearchProduct}
            onSelect={(value, option) => onSelectProduct(index, value, option)}
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (_: number | undefined, _record: ProductItem, index: number) => (
        <Form.Item
          name={[index, 'quantity']}
          className=" w-full"
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: 'Vui lòng nhập số lượng' }]}
          noStyle
        >
          <InputNumber className="w-full text-right" min={0} controls={false} />
        </Form.Item>
      ),
    },
    {
      title: 'Giá bán thực tế',
      dataIndex: 'price',
      key: 'price',
      width: 200,
      render: (_: number | undefined, _record: ProductItem, index: number) => {
        const product = formProducts[index] || {};
        const minPrice = Number(product.minPrice || 0);
        const maxPrice = Number(product.maxPrice || 0);
        const showRange = minPrice !== 0 || maxPrice !== 0;
        return (
          <div className="relative">
            <Form.Item
              name={[index, 'price']}
              className=" w-full"
              wrapperCol={{ span: 24 }}
              rules={[{ required: true, message: 'Không được để trống' }]}
              noStyle
            >
              <InputNumber
                className="w-full"
                formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                controls={false}
              />
            </Form.Item>
            {showRange && (
              <span className="absolute left-0 top-full mt-1 flex items-center text-xs text-amber-600">
                <DollarSign className="mr-1 h-3 w-3" />
                {formatNumberWithCommas(minPrice)} - {formatNumberWithCommas(maxPrice)} ₫
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'VAT',
      dataIndex: 'vat',
      key: 'vat',
      width: 100,
      render: (_: number | undefined, record: ProductItem, index: number) => (
        <Form.Item
          name={[index, 'vat']}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: '' }]}
          noStyle
          className="[&_.ant-input-number-input]:!text-right"
        >
          <InputNumber className="w-full text-right" controls={false} value={record.vat} disabled suffix="%" />
        </Form.Item>
      ),
    },
    {
      title: 'Thuế GTGT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      width: 150,
      render: (_: number | undefined, record: ProductItem, index: number) => (
        <Form.Item
          name={[index, 'vatAmount']}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: '' }]}
          noStyle
          className="w-full [&_.ant-input-number-input]:!text-right"
        >
          <InputNumber
            className="w-full"
            formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value?: string) => (value ? +value.replace(/\$\s?|(,*)/g, '') : 0)}
            controls={false}
            value={record.vatAmount}
            disabled
            suffix="đ"
          />
        </Form.Item>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 70,
      render: (_: any, _record: ProductItem, index: number) => (
        <Button type="text" icon={<Trash2 color="red" />} onClick={() => remove(index)} />
      ),
    },
  ];
}
